import { ClipProvider, MonetizationType, ObjectType, Show, TitleListType } from '@/@types/graphql-types';
import type { BuyBoxOffers } from '@/features/buybox/composables/useBuyboxData';
import type { SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import type { PosterTitle } from '@/interfaces/components/poster/PosterGraphql';
import type { TitleDetail } from '@/interfaces/title-details-graphql';

export interface TitleEventPayloadGraphql {
	title: {
		objectType: ObjectType;
		objectId: number;
		content?: {
			seasonNumber: number;
		};
		show?: {
			objectType: ObjectType;
			objectId: number;
		};
	};
	index?: number;
	date?: string;
	providerId?: number;
	label?: string;
}

/** EXPORT */
export function formatRuntime(runtime: number) {
	const minutes = runtime % 60;
	const hours = (runtime - minutes) / 60;

	const result = {
		translationKey: 'WEBAPP_DURATION_MINUTES',
		minutes,
		hours,
	};

	if (runtime >= 60) {
		result.translationKey = 'WEBAPP_DURATION';
	}

	return result;
}

export function getSelfHostedTrailerClips(title?: TitleDetail) {
	if (!title) return null;

	const clipsContainer = title.__typename === 'Season' ? title.show : title;

	let clips = clipsContainer.content.justwatchtvClips.filter(clip => !!clip.sources.dynamicManifest?.fileGroupId);

	// Self-hosted JWTV trailers
	if (!clips || clips.length === 0) clips = clipsContainer.content.justwatchtvClips;
	if (!clips || clips.length === 0) clips = clipsContainer.fallBackClips.justwatchtvClips;

	if (
		clips.length > 0 &&
		clips[0]?.provider === ClipProvider.Justwatchtv &&
		!!clips[0].sources?.dynamicManifest?.fileGroupId
	) {
		return clips;
	}

	return null;
}

export function getTVODBackdropClips(title: TitleDetail) {
	const container = title.__typename === 'Season' ? title.show : title;
	const dailymotionClips = container.content.dailymotionClips;
	if (dailymotionClips.length) return dailymotionClips;
	const fallbackDailymotionClips = container.fallBackClips.dailymotionClips;
	if (fallbackDailymotionClips.length) return fallbackDailymotionClips;
	const youtubeClips = container.content.clips.filter(clip => clip.provider === ClipProvider.Youtube);
	if (youtubeClips.length) return youtubeClips;
	return container.fallBackClips.clips.filter(clip => clip.provider === ClipProvider.Youtube);
}

/** Look for a monetized trailer clip if available. */
export function getBackdropClips(title?: TitleDetail) {
	if (!title) return [];

	const clipsContainer = title.__typename === 'Season' ? title.show : title;

	let clips = clipsContainer.content.dailymotionClips;

	// Monetized third-party trailers
	if (!clips || clips.length === 0) clips = clipsContainer.content.dailymotionClips;
	if (!clips || clips.length === 0) clips = clipsContainer.fallBackClips.dailymotionClips;
	if (!clips || clips.length === 0) clips = clipsContainer.content.videobusterClips;
	if (!clips || clips.length === 0) clips = clipsContainer.fallBackClips.videobusterClips;
	// Show has no trailers, try to get it from season
	if ((!clips || clips.length === 0) && title.__typename === 'Show')
		clips =
			title?.seasons.find(season => season.fallBackClips.dailymotionClips.length > 0)?.fallBackClips
				.dailymotionClips || [];
	if ((!clips || clips.length === 0) && title.__typename === 'Show')
		clips =
			title?.seasons.find(season => season.fallBackClips.videobusterClips.length > 0)?.fallBackClips
				.videobusterClips || [];

	// No monetized trailers at all, final fallback to YouTube
	if (!clips || clips.length === 0) clips = title.content.clips;
	if (!clips || clips.length === 0) clips = clipsContainer.fallBackClips.clips;
	if ((!clips || clips.length === 0) && title.__typename === 'Show')
		clips = title?.seasons.find(season => season.fallBackClips.clips.length > 0)?.fallBackClips.clips || [];

	return clips ?? [];
}

export function trackTitleEventGraphql(
	evtName: string,
	payload: TitleEventPayloadGraphql,
	evtProperty: string,
	contexts?: SnowplowContext[]
) {
	const { title, index, date = '', providerId = '', label = '' } = payload;

	// if we have a date/provider_id in the payload we use them as label for BI, ortherwise we use title
	const trackingLabel = label || `${date},${providerId}`;

	const properties = {
		action: evtName,
		label: trackingLabel,
		value: index,
		property: evtProperty,
	};

	let { objectId, objectType } = title;

	if (title?.show) {
		objectId = title.show.objectId;
		objectType = title.show.objectType;
	}
	// we attach filter context and title context (also Development)
	const titleContext = TrackingHelper.getTitleContextGraphql(
		objectId,
		objectType,
		title?.content?.seasonNumber,
		undefined,
		undefined,
		index
	);
	const additionalContexts: SnowplowContext[] = (contexts || []).concat([titleContext]);
	TrackingHelper.trackEvent('userinteraction', properties, additionalContexts);
}

export function isTitleSeason(node?: PosterTitle): node is PosterTitle & { __typename: 'Season' } {
	return node?.__typename === 'Season' && !!node.show;
}

export function isTitleShow(node?: PosterTitle): node is PosterTitle & { __typename: 'Show' } & Show {
	return node?.__typename === 'Show';
}

export function isTitleInList(listName: TitleListType, title: PosterTitle) {
	switch (listName) {
		case TitleListType.WatchNext:
			const _title = title.__typename === 'Season' ? title.show : title;
			return !!_title?.watchlistEntryV2?.createdAt;
		case TitleListType.Likelist:
			return !!title?.likelistEntry?.createdAt;
		case TitleListType.Dislikelist:
			return !!title?.dislikelistEntry?.createdAt;
		case TitleListType.CaughtUp:
			return title?.__typename === 'Movie'
				? !!title?.seenlistEntry?.createdAt
				: title?.seenState?.progress === 100;
		default:
			return false;
	}
}

export function extractBuyboxOffers(titleDetailNode: TitleDetail): BuyBoxOffers {
	return {
		dvdBluray: titleDetailNode?.offersDvdBluray ?? [],
		buy: titleDetailNode?.offers.filter(offer => [MonetizationType.Buy].includes(offer.monetizationType)),
		rent: titleDetailNode?.offers.filter(offer => [MonetizationType.Rent].includes(offer.monetizationType)),
		flatrate: titleDetailNode?.offers.filter(offer =>
			[MonetizationType.Flatrate, MonetizationType.FlatrateAndBuy, MonetizationType.Cinema].includes(
				offer.monetizationType
			)
		),
		// EXP_LINEAR_TV
		linear: titleDetailNode?.offers.filter(offer =>
			[MonetizationType.Fast, MonetizationType.LinearFlatrate, MonetizationType.LinearFree].includes(
				offer.monetizationType
			)
		),
		// EXP_LINEAR_TV
		free: titleDetailNode?.offers.filter(offer =>
			[MonetizationType.Free, MonetizationType.Ads].includes(offer.monetizationType)
		),
		offerCount: titleDetailNode?.offerCount ?? 0,
		maxOfferUpdatedAt: titleDetailNode?.maxOfferUpdatedAt,
		offersHistory: titleDetailNode?.offersHistory ?? [],
		__typename: titleDetailNode?.__typename,
		id: titleDetailNode?.id,
		bundles: titleDetailNode?.bundles ?? [],
		promotedBundles: titleDetailNode && 'promotedBundles' in titleDetailNode ? titleDetailNode.promotedBundles : [],
		promotedOffers: titleDetailNode?.promotedOffers ?? [],
		totalPackages: titleDetailNode && 'totalPackages' in titleDetailNode ? titleDetailNode.totalPackages : 0,
	};
}

export function extractAVODOffer(titleDetailNode: TitleDetail) {
	const { free } = extractBuyboxOffers(titleDetailNode);
	return free?.find(el => el.package.technicalName === 'plexplayer') || null;
}
